import { useEffect, useRef, useState } from 'react'

import type { VuiComponent } from '../core'
import type { SvgProps, SvgState } from './svg.types'

import { css, styled, vui } from '../core'
import { cs } from '../utils'
import cache from './cache'
import { getAttributes, getSvgContent, initState, parseSvg } from './helpers'

export const SvgBase: VuiComponent<'svg', SvgProps> = styled.svgBox<SvgProps>(({ pathFill }) => {
  return css`
    ${
      pathFill !== undefined &&
      (Array.isArray(pathFill)
        ? pathFill
            .map(
              (fill, index) => `
					& > path:nth-child(${index + 1}) {
						fill: ${fill};
					}
				`,
            )
            .join()
        : `
				& > path {
					fill: ${pathFill};
				}
			`)
    }
  `
})

/**
 * Displays an svg element based on provided children or 'src' prop.
 *
 * When using src, it loads the content from the given URL and saves in in cache.
 *
 * Loaded content is parsed with DOMParser (inert) and sanitized against an
 * allowlist of SVG elements before being rendered via innerHTML.
 */
export const Svg = vui<'svg', SvgProps>((props, ref) => {
  const { children, className, src, ...rest } = props
  const [svgState, setSvgState] = useState<SvgState>(() => initState())
  const isMountedRef = useRef(false)
  const srcRef = useRef(src)
  const prevContentRef = useRef<string>('')

  const { content = '', svgAttributes = {} } = svgState
  const innerHTML = !children ? { __html: content } : undefined

  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  useEffect(() => {
    srcRef.current = src

    async function loadSvg(src: string) {
      try {
        const response = await cache.get(src)
        const svgEl = parseSvg(response)
        const content = getSvgContent(svgEl)
        const svgAttributes = getAttributes(svgEl)

        svgAttributes['data-src'] = src ?? ''

        // Update state only when requested 'src' and current 'src' are the same
        if (srcRef.current === src && isMountedRef.current) {
          prevContentRef.current = content
          setSvgState({ content, svgAttributes })
        }
      } catch (error) {
        console.error(error)
        if (isMountedRef.current) {
          setSvgState(initState())
        }
      }
    }

    if (src) {
      // Preserve previous content while loading new src
      setSvgState(prev => ({ ...prev, content: prevContentRef.current }))
      void loadSvg(src)
    }
  }, [src])

  return (
    <SvgBase
      className={cs('vui-svg', className)}
      dangerouslySetInnerHTML={innerHTML}
      ref={ref}
      xmlns="http://www.w3.org/2000/svg"
      {...svgAttributes}
      {...rest}
    >
      {children}
    </SvgBase>
  )
})

Svg.displayName = 'Svg'

export default Svg
